/**
 * Onboarding Settings Page
 * Admin settings for the New User Setup Wizard
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Rocket, Settings, Save, Plus, Edit, Trash2, GripVertical,
  Eye, EyeOff, Video, Info, Target, Clock, Sparkles,
  ChevronUp, ChevronDown, Upload, ExternalLink, Image
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Step type options
const STEP_TYPES = [
  { value: 'NAVIGATION', label: 'Navigation', icon: Target },
  { value: 'ACTION', label: 'Action', icon: Sparkles },
  { value: 'VIDEO', label: 'Video', icon: Video },
  { value: 'INFO', label: 'Information', icon: Info },
  { value: 'COMING_SOON', label: 'Coming Soon', icon: Clock }
];

// Placement options
const PLACEMENTS = [
  { value: 'TOP', label: 'Top' },
  { value: 'BOTTOM', label: 'Bottom' },
  { value: 'LEFT', label: 'Left' },
  { value: 'RIGHT', label: 'Right' },
  { value: 'TOP_LEFT', label: 'Top Left' },
  { value: 'TOP_RIGHT', label: 'Top Right' },
  { value: 'BOTTOM_LEFT', label: 'Bottom Left' },
  { value: 'BOTTOM_RIGHT', label: 'Bottom Right' },
  { value: 'CENTER', label: 'Center (No Target)' }
];

// Media type options
const MEDIA_TYPES = [
  { value: 'NONE', label: 'None' },
  { value: 'YOUTUBE', label: 'YouTube' },
  { value: 'VIMEO', label: 'Vimeo' },
  { value: 'EXTERNAL_LINK', label: 'External Link' },
  { value: 'UPLOADED_VIDEO', label: 'Uploaded Video' },
  { value: 'IMAGE', label: 'Image' }
];

export const OnboardingSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Config state
  const [config, setConfig] = useState({
    enabled: true,
    auto_launch_on_first_login: true,
    show_banner_until_complete: true
  });
  
  // Steps state
  const [steps, setSteps] = useState([]);
  const [editingStep, setEditingStep] = useState(null);
  const [showStepDialog, setShowStepDialog] = useState(false);
  
  // Step form state
  const [stepForm, setStepForm] = useState({
    title: '',
    body_text: '',
    step_type: 'INFO',
    target_route: '',
    target_selector: '',
    placement: 'CENTER',
    media_type: 'NONE',
    media_url: '',
    is_coming_soon: false,
    is_active: true
  });

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [configRes, stepsRes] = await Promise.all([
          axios.get(`${API}/admin/onboarding/config?user_id=${user?.id}`),
          axios.get(`${API}/admin/onboarding/steps?user_id=${user?.id}&include_inactive=true`)
        ]);
        
        setConfig(configRes.data);
        setSteps(stepsRes.data);
      } catch (error) {
        console.error('Error fetching onboarding settings:', error);
        toast.error('Failed to load onboarding settings');
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.id) {
      fetchData();
    }
  }, [user?.id]);

  // Save config
  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/onboarding/config?user_id=${user?.id}`,
        config
      );
      setConfig(response.data);
      toast.success('Settings saved');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Open step dialog for editing
  const handleEditStep = (step) => {
    setEditingStep(step);
    setStepForm({
      title: step.title,
      body_text: step.body_text,
      step_type: step.step_type,
      target_route: step.target_route || '',
      target_selector: step.target_selector || '',
      placement: step.placement,
      media_type: step.media_type,
      media_url: step.media_url || '',
      is_coming_soon: step.is_coming_soon,
      is_active: step.is_active
    });
    setShowStepDialog(true);
  };

  // Open step dialog for new step
  const handleNewStep = () => {
    setEditingStep(null);
    setStepForm({
      title: '',
      body_text: '',
      step_type: 'INFO',
      target_route: '',
      target_selector: '',
      placement: 'CENTER',
      media_type: 'NONE',
      media_url: '',
      is_coming_soon: false,
      is_active: true
    });
    setShowStepDialog(true);
  };

  // Save step
  const handleSaveStep = async () => {
    if (!stepForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingStep) {
        // Update existing step
        const response = await axios.put(
          `${API}/admin/onboarding/steps/${editingStep.id}?user_id=${user?.id}`,
          stepForm
        );
        setSteps(prev => prev.map(s => s.id === editingStep.id ? response.data : s));
        toast.success('Step updated');
      } else {
        // Create new step
        const response = await axios.post(
          `${API}/admin/onboarding/steps?user_id=${user?.id}`,
          stepForm
        );
        setSteps(prev => [...prev, response.data]);
        toast.success('Step created');
      }
      setShowStepDialog(false);
    } catch (error) {
      console.error('Error saving step:', error);
      toast.error('Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  // Delete step
  const handleDeleteStep = async (step) => {
    if (!window.confirm(`Delete step "${step.title}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/onboarding/steps/${step.id}?user_id=${user?.id}`);
      setSteps(prev => prev.filter(s => s.id !== step.id));
      toast.success('Step deleted');
    } catch (error) {
      console.error('Error deleting step:', error);
      toast.error('Failed to delete step');
    }
  };

  // Move step up/down
  const handleMoveStep = async (step, direction) => {
    const currentIndex = steps.findIndex(s => s.id === step.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= steps.length) return;
    
    const newSteps = [...steps];
    [newSteps[currentIndex], newSteps[newIndex]] = [newSteps[newIndex], newSteps[currentIndex]];
    setSteps(newSteps);
    
    // Save new order
    try {
      await axios.post(
        `${API}/admin/onboarding/steps/reorder?user_id=${user?.id}`,
        { step_ids: newSteps.map(s => s.id) }
      );
    } catch (error) {
      console.error('Error reordering steps:', error);
      toast.error('Failed to save order');
    }
  };

  // Toggle step active status
  const handleToggleActive = async (step) => {
    try {
      const response = await axios.put(
        `${API}/admin/onboarding/steps/${step.id}?user_id=${user?.id}`,
        { is_active: !step.is_active }
      );
      setSteps(prev => prev.map(s => s.id === step.id ? response.data : s));
    } catch (error) {
      console.error('Error toggling step:', error);
      toast.error('Failed to update step');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-64 bg-slate-200 dark:bg-slate-800 rounded" />
            <div className="h-40 bg-slate-200 dark:bg-slate-800 rounded-lg" />
            <div className="h-96 bg-slate-200 dark:bg-slate-800 rounded-lg" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="onboarding-settings">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Rocket className="h-7 w-7 text-blue-500" />
              Onboarding Settings
            </h1>
            <p className="text-muted-foreground">
              Configure the new user setup wizard
            </p>
          </div>
        </div>

        <Tabs defaultValue="config">
          <TabsList>
            <TabsTrigger value="config">
              <Settings className="h-4 w-4 mr-2" />
              Configuration
            </TabsTrigger>
            <TabsTrigger value="steps">
              <GripVertical className="h-4 w-4 mr-2" />
              Steps ({steps.length})
            </TabsTrigger>
          </TabsList>

          {/* Configuration Tab */}
          <TabsContent value="config" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Global Settings</CardTitle>
                <CardDescription>
                  Control how the onboarding wizard behaves for new users
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Wizard */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable New User Setup Wizard</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      When enabled, new users will see the onboarding wizard
                    </p>
                  </div>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>

                {/* Auto-launch */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Auto-launch on First Login</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Automatically show the wizard when a new user logs in for the first time
                    </p>
                  </div>
                  <Switch
                    checked={config.auto_launch_on_first_login}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, auto_launch_on_first_login: checked }))}
                    disabled={!config.enabled}
                  />
                </div>

                {/* Show Banner */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Show Banner Until Complete</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Display a banner prompting users to resume onboarding if they paused
                    </p>
                  </div>
                  <Switch
                    checked={config.show_banner_until_complete}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, show_banner_until_complete: checked }))}
                    disabled={!config.enabled}
                  />
                </div>

                <Button onClick={handleSaveConfig} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Steps Tab */}
          <TabsContent value="steps" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Onboarding Steps</CardTitle>
                    <CardDescription>
                      Define the steps users will see during onboarding
                    </CardDescription>
                  </div>
                  <Button onClick={handleNewStep}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Step
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {steps.map((step, index) => {
                      const StepIcon = STEP_TYPES.find(t => t.value === step.step_type)?.icon || Info;
                      return (
                        <TableRow key={step.id} className={!step.is_active ? 'opacity-50' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleMoveStep(step, 'up')}
                                disabled={index === 0}
                              >
                                <ChevronUp className="h-4 w-4" />
                              </Button>
                              <span className="font-medium">{index + 1}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleMoveStep(step, 'down')}
                                disabled={index === steps.length - 1}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <StepIcon className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{step.title}</span>
                              {step.is_coming_soon && (
                                <Badge variant="outline" className="text-xs">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Coming Soon
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{step.step_type}</Badge>
                          </TableCell>
                          <TableCell>
                            {step.target_selector ? (
                              <code className="text-xs bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                                {step.target_selector}
                              </code>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(step)}
                            >
                              {step.is_active ? (
                                <Eye className="h-4 w-4 text-green-500" />
                              ) : (
                                <EyeOff className="h-4 w-4 text-slate-400" />
                              )}
                            </Button>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditStep(step)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-600"
                                onClick={() => handleDeleteStep(step)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Step Edit Dialog */}
        <Dialog open={showStepDialog} onOpenChange={setShowStepDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingStep ? 'Edit Step' : 'New Step'}
              </DialogTitle>
              <DialogDescription>
                Configure the onboarding step details
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Title */}
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={stepForm.title}
                  onChange={(e) => setStepForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Create Your First Lead"
                />
              </div>

              {/* Body Text */}
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={stepForm.body_text}
                  onChange={(e) => setStepForm(prev => ({ ...prev, body_text: e.target.value }))}
                  placeholder="Instructions for this step..."
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Step Type */}
                <div className="space-y-2">
                  <Label>Step Type</Label>
                  <Select
                    value={stepForm.step_type}
                    onValueChange={(value) => setStepForm(prev => ({ ...prev, step_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STEP_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Placement */}
                <div className="space-y-2">
                  <Label>Bubble Placement</Label>
                  <Select
                    value={stepForm.placement}
                    onValueChange={(value) => setStepForm(prev => ({ ...prev, placement: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLACEMENTS.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Target Route */}
              <div className="space-y-2">
                <Label>Target Route (optional)</Label>
                <Input
                  value={stepForm.target_route}
                  onChange={(e) => setStepForm(prev => ({ ...prev, target_route: e.target.value }))}
                  placeholder="e.g., /leads or /dashboard"
                />
                <p className="text-xs text-muted-foreground">
                  Page to navigate to for this step
                </p>
              </div>

              {/* Target Selector */}
              <div className="space-y-2">
                <Label>Target Selector (optional)</Label>
                <Input
                  value={stepForm.target_selector}
                  onChange={(e) => setStepForm(prev => ({ ...prev, target_selector: e.target.value }))}
                  placeholder="e.g., new-lead-btn"
                />
                <p className="text-xs text-muted-foreground">
                  data-onboard attribute value to highlight
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Media Type */}
                <div className="space-y-2">
                  <Label>Media Type</Label>
                  <Select
                    value={stepForm.media_type}
                    onValueChange={(value) => setStepForm(prev => ({ ...prev, media_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MEDIA_TYPES.map(m => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Media URL */}
                {stepForm.media_type !== 'NONE' && (
                  <div className="space-y-2">
                    <Label>Media URL</Label>
                    <Input
                      value={stepForm.media_url}
                      onChange={(e) => setStepForm(prev => ({ ...prev, media_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                )}
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_coming_soon"
                    checked={stepForm.is_coming_soon}
                    onCheckedChange={(checked) => setStepForm(prev => ({ ...prev, is_coming_soon: checked }))}
                  />
                  <Label htmlFor="is_coming_soon">Mark as Coming Soon</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_active"
                    checked={stepForm.is_active}
                    onCheckedChange={(checked) => setStepForm(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStepDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveStep} disabled={saving}>
                {saving ? 'Saving...' : 'Save Step'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default OnboardingSettings;
